import { useLingui } from '@lingui/react/macro';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '../lib/scroll-lock.js';

// Shared right-side drawer shell (s216-54h.71).
//
// Every drawer in the dashboard is the same chrome: a full-height panel
// pinned to the right edge with the plum hard-shadow, sliding in over a
// dimmed scrim, closed by ESC / scrim / a close button, with body
// scroll locked while open. Before this, ~12 components each hand-rolled
// that boilerplate (the visible-on-next-frame state, the two nested
// divs, the 260ms transitions, the ESC listener). They drifted — one
// used a paper bg + soft shadow, widths ranged 480–680 — and every new
// drawer copy-pasted the lot.
//
// This is the single source of truth. A drawer component owns its
// header / body / footer and the data; the shell owns the chrome:
//
//   const { visible, requestClose } = useDrawer(onClose);
//   // mutations call requestClose() in onSuccess so they animate out
//   return (
//     <Drawer visible={visible} onRequestClose={requestClose} ariaLabelledBy="x-title">
//       <header>…<CloseButton onClick={requestClose} /></header>
//       <div className="flex-1 overflow-y-auto …">…</div>
//       <footer>…</footer>
//     </Drawer>
//   );

/** How long the exit runs before onClose fires.
 *
 *  THIS NUMBER IS WRITTEN IN FOUR PLACES and nothing keeps them in sync: here,
 *  and as `duration-[260ms]` in Drawer's scrim, Drawer's panel and Modal's
 *  pair. Tailwind's JIT needs a literal in the class string, so the constant
 *  cannot be interpolated into them.
 *
 *  Change one and you must change all four. Raise this alone and onClose fires
 *  after the animation has already finished, leaving a frozen panel; raise a
 *  class alone and the panel is unmounted mid-exit. Spec 748d30e4 RES-3.
 *
 *  Verified 2026-10-01 while adding Modal: the hook is NOT coupled to the
 *  drawer's translate-x specifically — it is this shared duration — which is
 *  what let a centred modal reuse the same lifecycle (thesis T-1). */
const DRAWER_ANIM_MS = 260;

/** Drives the slide-in/out lifecycle. `visible` flips true on the frame
 *  after mount so the CSS transition runs; `requestClose` plays the
 *  slide-out then calls `onClose` once the animation finishes. Call
 *  `requestClose` from footer actions / mutation onSuccess so the drawer
 *  animates away instead of snapping shut. */
export function useDrawer(onClose: () => void): { visible: boolean; requestClose: () => void } {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);
  // onClose is read through a ref so requestClose keeps a stable enough
  // contract even if the caller passes a fresh closure each render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const requestClose = () => {
    setVisible(false);
    window.setTimeout(() => onCloseRef.current(), DRAWER_ANIM_MS);
  };
  return { visible, requestClose };
}

export interface DrawerProps {
  /** From useDrawer — drives the slide transition. */
  visible: boolean;
  /** From useDrawer — scrim click + ESC call this. */
  onRequestClose: () => void;
  /** Tailwind max-width for the panel. Default `max-w-[640px]`. */
  maxWidthClass?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  /** Extra classes appended to the panel — rare (e.g. a drawer that
   *  needs a different background). */
  panelClassName?: string;
  children: ReactNode;
}

export function Drawer({
  visible,
  onRequestClose,
  maxWidthClass = 'max-w-[640px]',
  ariaLabel,
  ariaLabelledBy,
  panelClassName = '',
  children,
}: DrawerProps) {
  useBodyScrollLock(true);

  // ESC closes. Read the latest handler through a ref so we subscribe
  // the keydown listener once, not on every render.
  const closeRef = useRef(onRequestClose);
  closeRef.current = onRequestClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Portal to <body> so the drawer always positions against the
  // viewport. Drawers mount from anywhere — including deep inside the
  // home rail, whose ancestors set transform/overflow, which would
  // otherwise make `position: fixed` resolve against that ancestor (and
  // clip the panel). The formerly-portaled chat drawers relied on this;
  // doing it once here makes every drawer robust to its mount location.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className="fixed inset-0 z-50"
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: scrim is intentionally non-interactive for assistive tech; ESC + close button cover keyboard users */}
      <div
        onMouseDown={onRequestClose}
        className={`absolute inset-0 bg-[var(--ink)]/30 transition-opacity duration-[260ms] ease-out ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        className={`absolute right-0 top-0 flex h-full w-full ${maxWidthClass} flex-col overflow-hidden border-l-2 border-[var(--ink)] bg-[var(--cream)] pb-[env(safe-area-inset-bottom)] shadow-[-8px_0_0_var(--plum)] transition-transform duration-[260ms] ease-out ${
          visible ? 'translate-x-0' : 'translate-x-full'
        } ${panelClassName}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** The centred counterpart to Drawer, over the SAME lifecycle.
 *
 *  useDrawer is geometry-agnostic despite its name — it owns when a panel is
 *  visible and when its exit has finished, not where the panel sits. That is
 *  spec 748d30e4's thesis T-1, and it is why this needed no second hook.
 *
 *  Exists because four overlays render `items-center justify-center` and
 *  putting them behind Drawer would slide a destructive-confirmation dialog in
 *  from the right edge. The founder asked for animation, not relocation.
 *
 *  The motion differs from Drawer's on purpose: a right-pinned panel slides,
 *  a centred one fades and lifts. Same 260ms ease-out, so the two read as one
 *  vocabulary rather than two.
 *
 *  SCRIM IS --ink/30, matching Drawer. The centred components used /35; a 5%
 *  difference nobody can see is not worth keeping two numbers for. */
export function Modal({
  visible,
  onRequestClose,
  maxWidthClass = 'max-w-[420px]',
  ariaLabel,
  ariaLabelledBy,
  panelClassName = '',
  align = 'center',
  zClassName = 'z-50',
  children,
}: DrawerProps & {
  /** Stacking layer. Default z-50 matches Drawer. A modal opened FROM inside
   *  another overlay needs to sit above it — WontImplementModal lives inside
   *  the feed-card drawer and used z-[60]; inheriting z-50 would have put a
   *  confirmation dialog behind the thing it was confirming. */
  zClassName?: string;
  /** 'center' for short dialogs; 'start' for tall forms that scroll.
   *
   *  A 600px form vertically centred in a 700px viewport has its top and
   *  bottom cropped with no way to reach them — rsa-swap-modal,
   *  new-rsa-modal and new-ad-group-modal all chose items-start for exactly
   *  that reason, and all three carry overflow-y-auto. Centring them would
   *  have been a regression dressed as consistency. */
  align?: 'center' | 'start';
}) {
  useBodyScrollLock(true);

  const closeRef = useRef(onRequestClose);
  closeRef.current = onRequestClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={`fixed inset-0 ${zClassName} flex justify-center px-4 sm:px-6 ${
        align === 'start' ? 'items-start overflow-y-auto py-8' : 'items-center'
      }`}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: scrim is intentionally non-interactive for assistive tech; ESC + the dialog's own cancel button cover keyboard users */}
      <div
        onMouseDown={onRequestClose}
        className={`fixed inset-0 bg-[var(--ink)]/30 transition-opacity duration-[260ms] ease-out ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        className={`relative w-full ${maxWidthClass} transition-all duration-[260ms] ease-out ${
          visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-[0.98] opacity-0'
        } ${panelClassName}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** The standard drawer close button — the ✕ in a bordered chip that
 *  sits in the top-right of every drawer header. Extracted so the
 *  identical 18px svg isn't pasted into each header. */
export function DrawerCloseButton({ onClick }: { onClick: () => void }) {
  const { t } = useLingui();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t`close`}
      className="shrink-0 rounded-md border border-[var(--line)] bg-white p-1 text-[var(--ink)]/55 hover:text-[var(--ink)]"
    >
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <title>{t`close`}</title>
        <path
          d="M4 4l10 10M14 4L4 14"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
